const ExchangeTips = () => {
  const tips = [
    {
      title: 'Compare Multiple Locations',
      description: 'Exchange rates can vary by 0.50-1.00 peso between different locations. Shop around to find the best deal.',
      icon: 'ri-search-line',
      category: 'Strategy'
    },
    {
      title: 'Avoid Airport Exchanges',
      description: 'Airport exchange counters typically offer rates 5-10% worse than city locations due to convenience fees.',
      icon: 'ri-plane-line',
      category: 'Location'
    },
    {
      title: 'Exchange Larger Amounts',
      description: 'Many locations offer better rates for exchanges over $100 USD. Consolidate your exchanges when possible.',
      icon: 'ri-money-dollar-circle-line',
      category: 'Amount'
    },
    {
      title: 'Check for Hidden Fees',
      description: 'Always ask about commission fees, service charges, and minimum exchange amounts before proceeding.',
      icon: 'ri-eye-line',
      category: 'Fees'
    },
    {
      title: 'Bring Clean, New Bills',
      description: 'Damaged, torn, or very old US bills may be rejected or accepted at lower rates. Bring crisp, new currency.',
      icon: 'ri-money-dollar-box-line',
      category: 'Quality'
    },
    {
      title: 'Time Your Exchange',
      description: 'Monitor rates for a few days before your trip. Exchange when the peso is weaker for better value.',
      icon: 'ri-time-line',
      category: 'Timing'
    }
  ];

  const warnings = [
    {
      title: 'Never Use Street Exchangers',
      description: 'Unofficial street money changers are illegal and dangerous. Stick to licensed establishments only.',
      level: 'danger'
    },
    {
      title: 'Count Your Money Carefully',
      description: 'Always count your pesos before leaving the counter. Verify the exchange rate used matches the quoted rate.',
      level: 'warning'
    },
    {
      title: 'Keep Your Receipt',
      description: 'Save exchange receipts for your records and in case you need to re-exchange unused pesos.',
      level: 'info'
    }
  ];

  return (
    <div className="mt-20">
      <div className="text-center mb-12">
        <h2 className="text-4xl font-bold text-zinc-900 mb-4">Expert Exchange Tips</h2>
        <p className="text-xl text-zinc-600 max-w-2xl mx-auto">
          Professional advice to help you get the best exchange rates and avoid common pitfalls
        </p>
      </div>

      {/* Tips Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
        {tips.map((tip, index) => (
          <div key={index} className="bg-white rounded-2xl p-8 border border-zinc-200 shadow-lg hover:shadow-xl transition-all">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 bg-emerald-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                <i className={`${tip.icon} text-xl text-white`}></i>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-lg font-bold text-zinc-900">{tip.title}</h3>
                  <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-semibold">
                    {tip.category}
                  </span>
                </div>
              </div>
            </div>
            <p className="text-zinc-600">{tip.description}</p>
          </div>
        ))}
      </div>

      {/* Important Warnings */}
      <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-3xl p-12">
        <h3 className="text-3xl font-bold text-zinc-900 mb-8 text-center">Important Safety Warnings</h3>
        <div className="grid md:grid-cols-3 gap-6">
          {warnings.map((warning, index) => (
            <div key={index} className={`bg-white rounded-2xl p-6 border-l-4 ${
              warning.level === 'danger' ? 'border-red-500' :
              warning.level === 'warning' ? 'border-yellow-500' :
              'border-blue-500'
            }`}>
              <div className="flex items-start gap-3 mb-3">
                <i className={`${
                  warning.level === 'danger' ? 'ri-error-warning-line text-red-500' :
                  warning.level === 'warning' ? 'ri-alert-line text-yellow-500' :
                  'ri-information-line text-blue-500'
                } text-xl flex-shrink-0 mt-1`}></i>
                <h4 className="font-bold text-zinc-900">{warning.title}</h4>
              </div>
              <p className="text-zinc-600 text-sm">{warning.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Best Practices Summary */}
      <div className="mt-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-3xl p-12 text-white">
        {/* Featured Image */}
        <div className="mb-12">
          <img
            src="https://static.readdy.ai/image/a7a45bef519addb86f62650fa4d00e15/75271c8b1fbc4fc61d97819f5d86a53b.jpeg"
            alt="USD to MXN Dollar to Peso Exchange Rate"
            className="w-full h-auto rounded-2xl shadow-2xl"
          />
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h3 className="text-3xl font-bold mb-6">Exchange Like a Pro</h3>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <i className="ri-check-line text-2xl text-emerald-200"></i>
                <span>Research rates at multiple locations before exchanging</span>
              </div>
              <div className="flex items-center gap-3">
                <i className="ri-check-line text-2xl text-emerald-200"></i>
                <span>Use licensed banks or registered casa de cambio only</span>
              </div>
              <div className="flex items-center gap-3">
                <i className="ri-check-line text-2xl text-emerald-200"></i>
                <span>Bring clean, undamaged US dollar bills</span>
              </div>
              <div className="flex items-center gap-3">
                <i className="ri-check-line text-2xl text-emerald-200"></i>
                <span>Count your pesos carefully before leaving</span>
              </div>
              <div className="flex items-center gap-3">
                <i className="ri-check-line text-2xl text-emerald-200"></i>
                <span>Keep receipts for your records</span>
              </div>
            </div>
          </div>
          <div>
            <img
              src="https://public.readdy.ai/ai/img_res/6e9ca966f438b8c16c113d5239fde318.jpg"
              alt="Successful currency exchange transaction"
              className="w-full h-80 object-cover rounded-2xl"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExchangeTips;